import { LinearProgress, Paper, Stack, Typography } from "@mui/material";

interface AnalysisRunStatusProps {
  /** Analyses in the run that have finished, successfully or not. */
  finishedCount: number;
  /** 0–100 across the whole run, counting snipping and partial progress of running tasks. */
  progress: number;
  /** Analyses still waiting for their plots to be snipped. */
  snippingCount: number;
  startedAt: Date | null;
  totalCount: number;
}

const formatStartedAt = (startedAt: Date): string =>
  startedAt.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

/** Shown in place of the run table while analyses are queued or running. */
const AnalysisRunStatus: React.FC<AnalysisRunStatusProps> = ({
  finishedCount,
  progress,
  snippingCount,
  startedAt,
  totalCount,
}) => (
  <Stack spacing={2}>
    <Paper sx={{ p: 2 }} variant="outlined">
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          sx={{ alignItems: "baseline", justifyContent: "space-between" }}
        >
          <Typography
            color="text.secondary"
            sx={{ letterSpacing: 1, textTransform: "uppercase" }}
            variant="overline"
          >
            Analysis running
          </Typography>
          {startedAt != null && (
            <Typography color="text.secondary" variant="body2">
              Started {formatStartedAt(startedAt)}
            </Typography>
          )}
        </Stack>
        <Typography component="h3" variant="subtitle1">
          {totalCount} {totalCount === 1 ? "analysis" : "analyses"} ·{" "}
          {finishedCount} finished
        </Typography>
        {snippingCount > 0 && (
          <Typography color="text.secondary" variant="body2">
            Snipping plots for {snippingCount}{" "}
            {snippingCount === 1 ? "analysis" : "analyses"} before they start.
          </Typography>
        )}
        <LinearProgress
          aria-label="Analysis progress"
          value={progress}
          variant="determinate"
        />
        <Typography variant="body2">
          This can take several minutes. You can keep working or close this
          location — results appear on the Results tab when it finishes.
        </Typography>
      </Stack>
    </Paper>
    <Typography color="text.secondary" variant="body2">
      Results will appear on the Results tab when the run finishes.
    </Typography>
  </Stack>
);

export default AnalysisRunStatus;
